import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

// Define types for different screen data
export interface OrderDetailsData {
  order: any[];
  orderID: string;
  Payment_Information: any;
  CustomerName: string;
  /** The linked back-office customer and their phone, for the M-Pesa prompt. */
  CustomerId?: string | null;
  CustomerPhone?: string | null;
  AssignedStaffInformation?: {
    _id: string;
    bp_employee_name: string;
    bp_employee_role: string;
  } | null;
  ReceiptID: string;
  ordertype: string;
  updatedAt: string;
}

// Interface for future navigation data types
interface NavigationData {
  orderDetails?: OrderDetailsData;
  // Future: productDetails?: ProductDetailsData;
  // Future: customerDetails?: CustomerDetailsData;
}

interface NavigationContextType {
  navigationData: NavigationData;
  setOrderDetailsData: (data: OrderDetailsData) => void;
  clearOrderDetailsData: () => void;
  clearAllNavigationData: () => void;
  // Future methods can be added here
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [navigationData, setNavigationData] = useState<NavigationData>({});

  const setOrderDetailsData = useCallback((data: OrderDetailsData) => {
    setNavigationData(prev => ({
      ...prev,
      orderDetails: data,
    }));
  }, []);

  const clearOrderDetailsData = useCallback(() => {
    setNavigationData(prev => ({
      ...prev,
      orderDetails: undefined,
    }));
  }, []);

  const clearAllNavigationData = useCallback(() => {
    setNavigationData({});
  }, []);

  const value = useMemo(
    () => ({
      navigationData,
      setOrderDetailsData,
      clearOrderDetailsData,
      clearAllNavigationData,
    }),
    [
      navigationData,
      setOrderDetailsData,
      clearOrderDetailsData,
      clearAllNavigationData,
    ],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export default NavigationContext;
